import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, FileText, Loader2, X } from 'lucide-react';
import type { ProjectFile } from '../../types';
import {
  defaultMergedPdfName,
  mergeProjectPdfs,
  summarizeMigrationCounts,
  type PdfMergeProgress,
  type PdfMergeSourceFile,
} from '../../utils/pdfMergeService';
import { portalHostedAssetHref } from '../../utils/portalAssetUrl';
import './ProjectPdfMergeModal.css';

export interface ProjectPdfMergeModalProps {
  projectId: string;
  dealId?: string;
  files: ProjectFile[];
  uploadedBy: string;
  uploadedByName: string;
  onClose: () => void;
  onComplete: (mergedFile: ProjectFile) => void;
}

const ProjectPdfMergeModal: React.FC<ProjectPdfMergeModalProps> = ({
  projectId,
  dealId,
  files,
  uploadedBy,
  uploadedByName,
  onClose,
  onComplete,
}) => {
  const [orderedIds, setOrderedIds] = useState<string[]>(() => files.map(f => f.id!).filter(Boolean));
  const [outputName, setOutputName] = useState('');
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<PdfMergeProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] = useState<string | null>(null);

  const fileMap = useMemo(() => {
    const map = new Map<string, ProjectFile>();
    for (const f of files) {
      if (f.id) map.set(f.id, f);
    }
    return map;
  }, [files]);

  const orderedFiles = useMemo(
    () => orderedIds.map(id => fileMap.get(id)).filter(Boolean) as ProjectFile[],
    [orderedIds, fileMap],
  );

  useEffect(() => {
    setOutputName(defaultMergedPdfName(
      orderedFiles.map(f => ({ id: f.id!, name: f.name, url: f.url })),
    ));
  }, [orderedFiles]);

  const move = useCallback((index: number, dir: -1 | 1) => {
    setOrderedIds(prev => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, []);

  const handleMerge = async () => {
    if (orderedFiles.length < 2) return;
    setRunning(true);
    setError(null);
    setResultSummary(null);
    try {
      const sources: PdfMergeSourceFile[] = orderedFiles.map(f => ({
        id: f.id || f.url,
        name: f.name,
        url: portalHostedAssetHref(f.url, { downloadName: f.name }),
      }));
      const result = await mergeProjectPdfs({
        projectId,
        sources,
        outputName,
        uploadedBy,
        uploadedByName,
        dealId,
        onProgress: setProgress,
      });
      const summary = summarizeMigrationCounts(result.migratedCounts);
      const pages = result.pageCounts.reduce((a, b) => a + b, 0);
      setResultSummary(`Created ${pages}-page merged PDF. Preserved ${summary}.`);
      onComplete(result.mergedFile);
    } catch (err) {
      console.error('PDF merge failed:', err);
      setError(err instanceof Error ? err.message : 'Merge failed');
    } finally {
      setRunning(false);
    }
  };

  return createPortal(
    <div className="pdf-merge-overlay" onClick={running ? undefined : onClose}>
      <div className="pdf-merge-modal" onClick={e => e.stopPropagation()} role="dialog" aria-labelledby="pdf-merge-title">
        <header className="pdf-merge-header">
          <div>
            <h2 id="pdf-merge-title">Merge PDFs</h2>
            <p className="pdf-merge-subtitle">
              Combines pages in order and moves comments, dimensions, markup, and take-offs to the new file.
            </p>
          </div>
          <button type="button" className="pdf-merge-close" onClick={onClose} disabled={running} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="pdf-merge-body">
          <label className="pdf-merge-field">
            <span className="pdf-merge-label">Output filename</span>
            <input
              className="pdf-merge-input"
              value={outputName}
              onChange={e => setOutputName(e.target.value)}
              disabled={running}
            />
          </label>

          <div className="pdf-merge-order-label">Page order (top to bottom)</div>
          <ol className="pdf-merge-list">
            {orderedFiles.map((file, index) => (
              <li key={file.id} className="pdf-merge-item">
                <span className="pdf-merge-item-index">{index + 1}</span>
                <FileText size={16} aria-hidden />
                <span className="pdf-merge-item-name">{file.name}</span>
                <div className="pdf-merge-item-actions">
                  <button
                    type="button"
                    className="pdf-merge-icon-btn"
                    disabled={running || index === 0}
                    onClick={() => move(index, -1)}
                    aria-label={`Move ${file.name} up`}
                  >
                    <ArrowUp size={14} />
                  </button>
                  <button
                    type="button"
                    className="pdf-merge-icon-btn"
                    disabled={running || index === orderedFiles.length - 1}
                    onClick={() => move(index, 1)}
                    aria-label={`Move ${file.name} down`}
                  >
                    <ArrowDown size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ol>

          {running && progress && (
            <div className="pdf-merge-progress">
              <Loader2 size={16} className="spin" aria-hidden />
              <span>{progress.message}</span>
            </div>
          )}

          {error && <p className="pdf-merge-error">{error}</p>}
          {resultSummary && <p className="pdf-merge-success">{resultSummary}</p>}
        </div>

        <footer className="pdf-merge-footer">
          <button type="button" className="pdf-merge-btn pdf-merge-btn-secondary" onClick={onClose} disabled={running}>
            {resultSummary ? 'Close' : 'Cancel'}
          </button>
          {!resultSummary && (
            <button
              type="button"
              className="pdf-merge-btn pdf-merge-btn-primary"
              disabled={running || orderedFiles.length < 2}
              onClick={() => void handleMerge()}
            >
              {running ? 'Merging…' : 'Merge & preserve overlays'}
            </button>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default ProjectPdfMergeModal;
